import type { BlockDTO } from '@next-space/fe-api-idl';
import { BlockStatus, QASearchSource } from '@next-space/fe-api-idl';
import Fuse from 'fuse.js';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import type { KeyboardListRef } from '../../components/keyboard-list';
import { KeyboardItemType, KeyboardList } from '../../components/keyboard-list';
import { checkAiSpaceStatus } from '../hooks/ai-limit-store';
import { useConversationContext } from '../hooks/context';
import { useConversationTools, useQAQueryOption, useSearchPageStore } from '../hooks/hook';
import type { MentionItem } from '../hooks/types';
import { ReferencePageType, SearchPageStatus } from '../hooks/types';
import { useChatTranslation } from '../hooks/use-i18n';
import { getPageTitleInfo } from '../hooks/use-search';
import { ApplySpaceAiButton } from './apply-space-ai';
import { ChatType } from '../types';

interface MentionListProps {
  items: MentionItem[];
  command: (item: MentionItem) => void;
  query?: string;
  onHeightChange?: () => void;
  editor: any;
}

interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const { t } = useChatTranslation();
  const {
    chatType,
    switchChatType,
    addSearchBlocks: addReferencePageId,
    setNetworkSearch,
  } = useConversationTools();
  const { pages, searchStatus } = useSearchPageStore();
  const pagesList = useMemo(
    () => Object.values(pages).filter((p) => p.status === BlockStatus.NORMAL),
    [pages]
  );
  const listRef = useRef<KeyboardListRef>(null);
  const { renderPageIcon, spaceId } = useConversationContext();
  const status = checkAiSpaceStatus(spaceId);

  const formatResult = useCallback(
    (page: BlockDTO) => ({
      id: page.uuid,
      title: getPageTitleInfo(page).title,
      type: KeyboardItemType.Mention,
      iconName: 'IcPages' as const,
      renderPageIcon: () => renderPageIcon?.(page),
      disabled: !status,
    }),
    [renderPageIcon, status]
  );

  const fuse = useMemo(() => {
    return new Fuse(pagesList, {
      keys: ['title'],
      threshold: 0.3,
    });
  }, [pagesList]);

  const filteredItems = useMemo(() => {
    if (props.query) {
      return fuse.search(props.query).map((result) => formatResult(result.item));
    }
    return pagesList.map(formatResult).slice(0, status ? 10 : 3);
  }, [props.query, pagesList, formatResult, status, fuse]);

  useEffect(() => {
    props.onHeightChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems.length]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      return listRef.current?.onKeyDown({ event }) ?? false;
    },
  }));

  const isEmpty = filteredItems.length === 0;
  const isLoading = searchStatus === SearchPageStatus.LOADING;

  const handleSelect = (item: typeof filteredItems[number]) => {
    props.command({
      id: item.id,
      label: item.title,
    });
    if (chatType !== ChatType.SPACE) {
      switchChatType(ChatType.SPACE);
    }

    addReferencePageId({
      uuid: item.id,
      type: ReferencePageType.PAGE,
    });
    setNetworkSearch(false);
  };

  return (
    <div className="next-modal text-normal rounded-lg flex flex-col py-1 text-t2 w-80">
      <div className="px-4 py-1 text-t4-medium text-grey3">{t('chatLinkToPage')}</div>
      {isEmpty ? (
        <div className="px-4 py-1 text-t4-medium text-grey3">
          {isLoading ? t('chatLoading') : t('chatNoResults')}
        </div>
      ) : (
        <KeyboardList
          className="max-h-[40vh]"
          ref={listRef}
          items={filteredItems}
          onClickItem={handleSelect}
        />
      )}
      {!status && (
        <div className="px-4 flex items-center flex-col justify-center pb-3 pt-4 space-y-2">
          <div>
            {"📃 Try to link page? You'll need to apply AI search for all personal sources first."}
          </div>
          <ApplySpaceAiButton className="w-fit" />
        </div>
      )}
    </div>
  );
});
